import React, { Fragment, useEffect } from "react";
import { oneOfType, array, string, bool, shape } from "prop-types";
import { connect, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";

import { actions as loansActions, selectors as loansSelectors } from "reducers/loans";
import { actions as settingsActions } from "reducers/settings";

import Notification from "pages/_components/Notification";
import MainContainer from "pages/_components/MainContainer";
import Container from "pages/_components/Container";
import Button from "pages/_components/Button";
import "react-circular-progressbar/dist/styles.css";
import FormattedDate from "pages/_components/FormattedDate";
import * as i18n from "util/i18n";
import { ResponsiveHeader, DesktopButtons, MobileButtons } from "pages/_components/header";
import Action from "pages/_components/Action";
import { isIOS } from "react-device-detect";
import CustomData from "../../_components/CustomData/CustomData";

const DetalleCuota = ({ fetching, loansList, detallePrestamo }) => {
    const dispatch = useDispatch();
    const params = useParams();
    useEffect(() => {
        dispatch(settingsActions.getUserData());
        dispatch(loansActions.getLoanDetail(params.id));
    }, []);

    const handleClick = (format) => {
        const cuota = detallePrestamo.installments?.find((x) => Number(x.feeNumber) === Number(params.cuota)) ?? null;
        const cuotaNumber = cuota.feeNumber;
        const iva = filtrarYSumarValores(detallePrestamo?.charges, null, cuotaNumber, "IVA");

        const percepcionIva = filtrarYSumarValores(detallePrestamo?.charges, null, cuotaNumber, "PERCEP_IVA");

        const bonificaciones =
            detallePrestamo.charges?.find((x) => x.code.toLocaleUpperCase() === "Bonificaciones".toLocaleUpperCase())
                ?.value ?? 0;

        const otrosImpuestos =
            detallePrestamo.charges?.find((x) => x.code.toLocaleUpperCase() === "Otros impuestos".toLocaleUpperCase())
                ?.value ?? 0;

        const sello = filtrarYSumarValores(detallePrestamo?.charges, null, cuotaNumber, "SELLADOS");

        const iibbCaba = filtrarYSumarValores(detallePrestamo?.charges, [302, 303, 309, 305], cuotaNumber, "IIBB");

        const iibb = filtrarYSumarValores(detallePrestamo?.charges, [300, 301, 310, 306], cuotaNumber, "IIBB");

        if (cuota != null) {
            const unsecuredLoanFeeDetail = {
                totalAmount: cuota?.importAmount ?? 0,
                feeNumber: params.cuota,
                periodicity: detallePrestamo?.periodicity.toLowerCase(),
                dueDate: cuota?.dueDate,
                capital: cuota?.capitalAmount ?? 0,
                compensatory: cuota?.compensatoryAmount ?? 0,
                interests: cuota?.interestsAmount ?? 0,
                punitorio: cuota?.accruedLateInterest ?? 0,
                iva: iva ?? 0,
                ivaPerception: percepcionIva ?? 0,
                otherTaxes: otrosImpuestos ?? 0,
                stampTax: sello ?? 0,
                bonuses: bonificaciones ?? 0,
                iibbCaba: iibbCaba ?? 0,
                iibbPcia: iibb ?? 0,
            };
            dispatch(loansActions.downloadUnsecuredLoanFeeDetail(unsecuredLoanFeeDetail, format));
        }
    };

    function filtrarYSumarValores(lista, chargeIDs, chargeInstallmentNumber, code) {
        const valoresFiltrados = lista
            .filter(
                (item) =>
                    item.chargeInstallmentNumber === chargeInstallmentNumber &&
                    item.code === code &&
                    (chargeIDs ? chargeIDs.includes(item.chargeId) : true),
            )
            .map((item) => item.value);

        const sumaValores = valoresFiltrados.reduce((total, valor) => total + valor, 0);

        return sumaValores;
    }
    const renderViewMore = () => {
        if (!detallePrestamo) {
            return null;
        }
        const cuota = detallePrestamo.installments?.find((x) => Number(x.feeNumber) === Number(params.cuota)) ?? null;
        if (!cuota) {
            return null;
        }
        const cuotaNumber = cuota.feeNumber;
        const iva = filtrarYSumarValores(detallePrestamo?.charges, null, cuotaNumber, "IVA");

        const percepcionIva = filtrarYSumarValores(detallePrestamo?.charges, null, cuotaNumber, "PERCEP_IVA");

        const bonificaciones =
            detallePrestamo.charges?.find((x) => x.code.toLocaleUpperCase() === "Bonificaciones".toLocaleUpperCase())
                ?.value ?? 0;

        const otrosImpuestos =
            detallePrestamo.charges?.find((x) => x.code.toLocaleUpperCase() === "Otros impuestos".toLocaleUpperCase())
                ?.value ?? 0;

        const sello = filtrarYSumarValores(detallePrestamo?.charges, null, cuotaNumber, "SELLADOS");

        const iibbCaba = filtrarYSumarValores(detallePrestamo?.charges, [302, 303, 309, 305], cuotaNumber, "IIBB");

        const iibb = filtrarYSumarValores(detallePrestamo?.charges, [300, 301, 310, 306], cuotaNumber, "IIBB");

        return (
            <div className="transaction-ticket ticket">
                <div className="ticket__header" />
                <div className="ticket__body">
                    <CustomData titleText="Total cuota" valueText={cuota?.importAmount ?? 0} valueTextType="ARS" />
                    <div className="ticket-section three-cols">
                        <CustomData titleText={i18n.get("", "Nº de cuota")} valueText={params.cuota} />
                        <CustomData
                            titleText={i18n.get("", "Vencimiento")}
                            valueText={<FormattedDate date={cuota?.paymentDueDate} timeZone="UTC" />}
                        />
                        <CustomData
                            titleText="unsecuredLoanForm.periodicity.label"
                            titleTextType="I18N"
                            valueText={i18n.get(
                                `loans.periodicity.${detallePrestamo?.periodicity.toLowerCase()}`,
                                "Mensual",
                            )}
                        />
                    </div>
                    <div className="ticket__divider inside-ticket__body" />
                    <div className="ticket-section three-cols">
                        <CustomData
                            titleText={i18n.get("menu.loans.detail.Capital", "Capital")}
                            valueText={cuota?.capitalAmount || 0}
                            valueTextType="ARS"
                        />
                        <CustomData
                            titleText={i18n.get("menu.loans.detail.Intereses", "Intereses")}
                            valueText={cuota?.interestsAmount || 0}
                            valueTextType="ARS"
                        />
                        <CustomData
                            titleText={i18n.get("menu.loans.detail.Compensatorio", "Compensatorio")}
                            valueText={cuota?.compensatoryAmount || 0}
                            valueTextType="ARS"
                        />
                        <CustomData
                            titleText={i18n.get("menu.loans.detail.Punitorio", "Punitorio")}
                            valueText={cuota?.accruedLateInterest || 0}
                            valueTextType="ARS"
                        />
                        <CustomData
                            titleText={i18n.get("menu.loans.detail.iva", "IVA")}
                            valueText={iva || 0}
                            valueTextType="ARS"
                        />
                        <CustomData
                            titleText={i18n.get("menu.loans.detail.percepcionIva", "Percepción de IVA")}
                            valueText={percepcionIva || 0}
                            valueTextType="ARS"
                        />
                        <CustomData
                            titleText={i18n.get("menu.loans.detail.otherImpuestos", "Otros impuestos")}
                            valueText={otrosImpuestos || 0}
                            valueTextType="ARS"
                        />
                        <CustomData
                            titleText={i18n.get("menu.loans.detail.Bonificaciones", "Bonificaciones")}
                            valueText={bonificaciones || 0}
                            valueTextType="ARS"
                        />
                        <CustomData
                            titleText={i18n.get("menu.loans.detail.impuestoSueldo", "Impuesto de sello")}
                            valueText={sello || 0}
                            valueTextType="ARS"
                        />
                        <CustomData
                            titleText={i18n.get("menu.loans.detail.iibb", "IIBB CABA")}
                            valueText={iibbCaba || 0}
                            valueTextType="ARS"
                        />
                        <CustomData
                            titleText={i18n.get("menu.loans.detail.iibbPcia", "IIBB Pcia. de Buenos Aires")}
                            valueText={iibb || 0}
                            valueTextType="ARS"
                        />
                    </div>
                </div>
                <div className="ticket__footer" data-copytext={i18n.get("global.ticketCopyText")} />
            </div>
        );
    };
    const renderBody = () => {
        const prestamoActual = loansList.find((x) => x.number === params.id);
        if (prestamoActual === undefined || prestamoActual === null || !detallePrestamo) {
            return null;
        }
        const cuota = detallePrestamo?.installments?.find((x) => Number(x.feeNumber) === Number(params.cuota)) ?? null;
        if (!cuota) {
            return null;
        }
        const getStatusBackgroundColor = (status) => {
            if (status === "COBRADO") {
                return "#C8E6C9";
            }
            if (status === "VCDO_IMPAGO") {
                return "#FFCDD2";
            }
            return "#E1F5FE";
        };
        const getStatusString = (status) => {
            if (status === "COBRADO") {
                return "Pagada";
            }
            if (status === "VCDO_IMPAGO") {
                return "Vencida";
            }
            return "Activa";
        };
        return (
            <>
                <ResponsiveHeader
                    defaultTitle={i18n.get("menu.loans.details.title", "Detalle de la cuota")}
                    backLinkTo={`/loans/detallePrestamo/${params.id}`}
                    infoComponents={
                        <div
                            style={{
                                width: "85px",
                                height: "25px",
                                borderRadius: "10px",
                                marginLeft: "20px",
                                textAlign: "center",
                                backgroundColor: getStatusBackgroundColor(cuota.status),
                            }}>
                            {getStatusString(cuota.status)}
                        </div>
                    }>
                    <DesktopButtons>
                        <Button
                            className="btn btn-outline"
                            image="images/figma/icon/download.svg"
                            label="global.download"
                            onClick={() => handleClick("pdf")}
                            block={false}
                        />
                    </DesktopButtons>
                    <MobileButtons
                        image={isIOS ? "images/figma/icon/menu-ios-white.svg" : "images/figma/icon/menu-ios.svg"}>
                        <Action labelKey="global.download" handleClick={() => handleClick("pdf")} />
                    </MobileButtons>
                </ResponsiveHeader>
                <div className="above-the-fold">
                    <Fragment>
                        <Container className="container--layout flex-grow no-scrollable">{renderViewMore()}</Container>
                    </Fragment>
                </div>
            </>
        );
    };

    return (
        <MainContainer showLoader={fetching} className="desktop">
            <Notification scopeToShow="loans" />

            {renderBody()}
        </MainContainer>
    );
};

DetalleCuota.propTypes = {
    detallePrestamo: shape({}).isRequired,
    loansList: oneOfType([array]).isRequired,
    fetching: oneOfType([string, bool]),
};

DetalleCuota.defaultProps = {
    fetching: false,
};

const mapStateToProps = (state) => ({
    fetching: loansSelectors.getFetching(state),
    loansList: loansSelectors.getLoansV2(state),
    detallePrestamo: loansSelectors.getDetallePrestamo(state),
});

export default connect(mapStateToProps)(DetalleCuota);
